import { Component, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Customer } from 'src/app/classes/customer.class';
import { ProjectService } from 'src/app/services/project.service';
import { Project, isSelectableProject } from '../../../../classes/project.class';
import { CustomerService } from '../../../../services/customer.service';
import { InputComponent } from '../input/input.component';

@Component({
    selector: 'app-project-input',
    standalone: false,
    templateUrl: './project-input.component.html',
    styleUrl: './project-input.component.scss',
})
export class ProjectInputComponent extends InputComponent implements OnInit {
    private projectService = inject(ProjectService);
    private customerService = inject(CustomerService);

    projectsLoading = false;
    allProjectsLoaded = false;

    projectInput$ = new Subject<string>();
    projectFocus$ = new Subject<void>();

    allProjects: Project[] = [];
    projects: Project[] = [];
    filteredProjects: Project[] = [];
    customers: Customer[] = [];

    fcAfasCustomerId = this.formData.form.controls.planning.get('afas_customer_id') as FormControl;
    resultsForCustomer = false;

    private selectedProjectCustomerId: string | null = null;

    ngOnInit() {
        // Vastleggen zonder te wachten op de (trage) loadInitialProjectSelection-call, anders wordt het project ten onrechte gereset.
        if (this.fc.value) {
            this.selectedProjectCustomerId = this.fcAfasCustomerId.value ?? null;
        }

        // opdrachtgever name is only shown in the dropdown footer → look it up on focus, not on init
        this.subscriptions.add(
            this.projectFocus$
                .pipe(
                    switchMap(() => {
                        const afasCustomerId = this.fcAfasCustomerId.value;
                        return afasCustomerId
                            ? this.customerService
                                  .search(afasCustomerId, 150, { include_customers_without_area: true })
                                  .pipe(catchError(() => of([])))
                            : of([]);
                    })
                )
                .subscribe(customers => (this.customers = customers))
        );

        this.setupAutoComplete();
        this.setupFocusChanges();
        this.setupCustomerChanges();
        this.loadInitialProjectSelection();
    }

    get selectedCustomerName(): string {
        const afasCustomerId = this.fcAfasCustomerId.value;
        if (!afasCustomerId) {
            return '-';
        }

        return (
            this.customers.find(customer => customer.afas_customer_id?.toLowerCase() === afasCustomerId?.toLowerCase())
                ?.name ?? afasCustomerId
        );
    }

    loadInitialProjectSelection() {
        const projectId = this.fc.value as string;
        if (!projectId?.length) {
            return;
        }

        this.projectsLoading = true;
        this.subscriptions.add(
            this.projectService.getProject(projectId).subscribe({
                next: ({ data: project }) => {
                    if (!project?.afas_project_id?.length) {
                        return;
                    }

                    if (this.allProjects.some(p => p.afas_project_id === project.afas_project_id) === false) {
                        this.allProjects = [project, ...this.allProjects];
                    }

                    if (this.projects.some(p => p.afas_project_id === project.afas_project_id) === false) {
                        this.projects = [project, ...this.projects];
                    }

                    if (this.filteredProjects.some(p => p.afas_project_id === project.afas_project_id) === false) {
                        this.filteredProjects = [project, ...this.filteredProjects];
                    }

                    this.selectedProjectCustomerId = project.afas_customer_id ?? null;
                },
                error: () => {
                    this.projectsLoading = false;
                },
                complete: () => {
                    this.projectsLoading = false;
                },
            })
        );
    }

    setupAutoComplete() {
        this.subscriptions.add(
            this.projectInput$.pipe(debounceTime(200)).subscribe(term => {
                const afasCustomerId = this.fcAfasCustomerId.value;
                let filtered = this.projects;
                if (term) {
                    filtered = filtered.filter(
                        p =>
                            p.afas_project_id?.toLowerCase().includes(term.toLowerCase()) ||
                            p.name?.toLowerCase().includes(term.toLowerCase()) ||
                            p.location?.toLowerCase().includes(term.toLowerCase())
                    );
                }
                // Alleen filteren op customer als die is gekozen
                if (afasCustomerId) {
                    filtered = filtered.filter(
                        p => p.afas_customer_id?.toLowerCase() === afasCustomerId?.toLowerCase()
                    );
                }
                this.filteredProjects = filtered;
            })
        );
    }

    setupFocusChanges() {
        this.subscriptions.add(
            this.projectFocus$
                .pipe(
                    switchMap(() => {
                        const afasCustomerId = this.fcAfasCustomerId.value;
                        if (this.allProjectsLoaded) {
                            let filteredProjects = this.allProjects;
                            if (afasCustomerId) {
                                filteredProjects = filteredProjects.filter(
                                    p => p.afas_customer_id?.toLowerCase() === afasCustomerId?.toLowerCase()
                                );
                            }
                            this.resultsForCustomer = afasCustomerId ? filteredProjects.length > 0 : true;
                            return of(filteredProjects);
                        }
                        return this.searchForProjects(afasCustomerId).pipe(
                            switchMap(projects => {
                                let filtered = projects;
                                if (afasCustomerId) {
                                    filtered = filtered.filter(
                                        p => p.afas_customer_id?.toLowerCase() === afasCustomerId?.toLowerCase()
                                    );
                                }
                                this.resultsForCustomer = afasCustomerId ? filtered.length > 0 : true;
                                return of(filtered);
                            })
                        );
                    })
                )
                .subscribe(projects => {
                    this.projects = projects;
                    this.filteredProjects = projects;
                })
        );
    }

    setupCustomerChanges() {
        this.subscriptions.add(
            this.fcAfasCustomerId.valueChanges
                .pipe(
                    debounceTime(500),
                    distinctUntilChanged((prev, curr) => prev?.toLowerCase() === curr?.toLowerCase())
                )
                .subscribe(newCustomerId => {
                    if (this.fc.value) {
                        const belongsToSelectedProject =
                            this.selectedProjectCustomerId?.toLowerCase() === newCustomerId?.toLowerCase();

                        if (belongsToSelectedProject) {
                            return;
                        }

                        this.fc.setValue(null);
                        this.selectedProjectCustomerId = null;
                    }

                    if (this.allProjectsLoaded) {
                        this.fc.setValue(null);
                        this.projectFocus$.next();
                    }
                })
        );
    }

    searchForProjects(afasCustomerId?: string): Observable<Project[]> {
        this.projectsLoading = true;
        if ((afasCustomerId === null || afasCustomerId === undefined) && this.allProjectsLoaded) {
            this.projectsLoading = false;
            return of(this.allProjects);
        }
        return this.projectService.searchProjects('', { afas_customer_id: afasCustomerId }, false, false).pipe(
            switchMap(projects => {
                const selectableProjects = projects.filter(isSelectableProject);
                this.projectsLoading = false;
                if (afasCustomerId !== null && afasCustomerId !== undefined) {
                    if (selectableProjects.length === 0) {
                        return this.searchForProjects();
                    }
                } else {
                    if (selectableProjects.length > 0) {
                        this.allProjects = selectableProjects;
                        this.allProjectsLoaded = true;
                        this.resultsForCustomer = false;
                        return of(selectableProjects);
                    }
                }
                this.resultsForCustomer = true;
                return of(selectableProjects);
            })
        );
    }

    onSelection(project: Project) {
        this.selectedProjectCustomerId = project?.afas_customer_id ?? null;

        if (!this.fcAfasCustomerId) {
            return;
        }

        if (project?.afas_customer_id && this.fcAfasCustomerId.value !== project.afas_customer_id) {
            this.fcAfasCustomerId.setValue(project.afas_customer_id);
        }
    }

    trackByFn(item: Project) {
        return item.afas_project_id;
    }
}
